import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ShortPA } from "../../utils/DTO/ProfileDTO";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { Spinner } from "react-bootstrap";
import { useDraggable } from "react-use-draggable-scroll";
import { useTranslation } from "react-i18next";

interface TabProps {
  title: string;
  disabled?: boolean;
  loading?: boolean;
  shortTab?: boolean;
}

const Tab: React.FC<TabProps> = ({ title, disabled, loading, shortTab }) => {
  const containerStyle = disabled
    ? {
        backgroundColor: "white",
        boxShadow: "0px 04px 38px -21px rgba(66, 66, 66, 1)",
        WebkitBoxShadow: "0px 04px 38px -21px rgba(66, 66, 66, 1)",
        MozBoxShadow: "0px 04px 38px -21px rgba(66, 66, 66, 1)",
        zIndex: 700,
        position: "relative" as React.CSSProperties["position"],
      }
    : {
        backgroundColor: "var(--background-color)",
        boxShadow: "inset 0px 10px 64px -67px rgba(66, 66, 66, 0.68)",
        WebkitBoxShadow: "inset 0px 10px 64px -67px rgba(66, 66, 66, 0.68)",
        MozBoxShadow: "inset 0px 10px 64px -67px rgba(66, 66, 66, 0.68)",
      };
  const border = {
    border: "1px solid var(--grey-100)",
    borderBottom: "0",
  };
  const [pressedLoading, setPressedLoading] = useState(false);
  useEffect(() => {}, [loading]);
  return (
    <div
      className="tab"
      style={{
        ...containerStyle,
        ...border,
        width: shortTab ? "50px" : "150px",
        height: "40px",
        borderRadius: "16px 16px 0 0",
        userSelect: "none" /* Standard syntax */,
      }}
      onClick={() => setPressedLoading(true)}
    >
      <div className="tab-title" style={{ textAlign: "center", fontWeight: "bold", paddingTop: "8px" }}>
        {loading && pressedLoading ? (
          <div style={{ width: "0px", height: "0px" }}>
            <Spinner animation="border" variant={"primary"} size="sm" style={{ position: "relative", left: 20, top: -3 }} />
          </div>
        ) : null}
        {title && title.charAt(0).toUpperCase() + title.slice(1)}
      </div>
    </div>
  );
};

interface PainAreaSelectionTabsProps {
  painAreaList: ShortPA[];
  userId: number;
  painAreaIdSelected?: number;
  onClick: (paId: number) => void;
  style?: React.CSSProperties;
  isMember?: boolean;
  onPlusAction?: () => void;
  isNewUI?: boolean;
  label?: string;
}

export const PainAreaSelectionTabs: React.FC<PainAreaSelectionTabsProps> = ({
  painAreaList,
  userId,
  painAreaIdSelected,
  onClick,
  style,
  isMember,
  onPlusAction,
  isNewUI,
  label,
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const history = useHistory();
  const [tabPressed, setTabPressed] = useState(false);
  const [pressedPaId, setPressedPaId] = useState<number | undefined>(undefined);
  const ref = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(ref);

  useEffect(() => {
    const handleScrollUpdate = () => {
      // setMaxScrollLeft(ref.current.scrollLeft);
    };
    ref.current.addEventListener("scroll", handleScrollUpdate);
    return () => {
      ref.current?.removeEventListener("scroll", handleScrollUpdate);
    };
  }, []);

  useEffect(() => {}, [tabPressed]);
  const isLoading = user?.isLoading && tabPressed;

  if (isNewUI) {
    return (
      <div className="pain-area-tabs--new-ui" style={{ ...style }}>
        <span className="pain-area-tabs__label">{label ?? t("dashboard.your_current_program")}</span>
        <div {...events} ref={ref} className="hideScroll pain-area-tabs__pills-row">
          {painAreaList.map(painArea => {
            const isSelected = painAreaIdSelected === painArea.id;
            return (
              <button
                key={painArea.id}
                id={painArea.painAreaName}
                className={`pain-area-tabs__pill${isSelected ? " pain-area-tabs__pill--selected" : ""}`}
                disabled={isSelected || isLoading}
                onClick={() => {
                  if (!isMember) history.push(`/admin/profile/${userId}/${painArea.id}`);
                  if (!isLoading) {
                    setTabPressed(true);
                    setPressedPaId(painArea.id);
                    onClick(painArea.id);
                  }
                }}
              >
                {isLoading && pressedPaId === painArea.id && (
                  <Spinner animation="border" variant="primary" size="sm" className="pain-area-tabs__pill-spinner" />
                )}
                {painArea.painAreaName && painArea.painAreaName.charAt(0).toUpperCase() + painArea.painAreaName.slice(1)}
              </button>
            );
          })}
          {onPlusAction && (
            <button className="pain-area-tabs__add-btn" disabled={isLoading} onClick={() => !isLoading && onPlusAction()}>
              + {t("dashboard.add_another_pain_area")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      {...events}
      ref={ref}
      className="hideScroll"
      style={{ display: "flex", overflowX: "scroll", width: "100%", flexDirection: "row", marginBottom: "-2px", ...style }}
    >
      {painAreaList.map(painArea => (
        <div style={{ marginRight: "8px" }} key={painArea.id} id={painArea.painAreaName}>
          {painAreaIdSelected === painArea.id ? (
            <div style={{ pointerEvents: "none", display: "flex" }}>
              <Tab title={painArea.painAreaName} disabled />
            </div>
          ) : (
            <div
              style={isLoading ? { cursor: "default", color: "gray" } : { cursor: "pointer", color: "gray" }}
              onClick={() => {
                if (!isMember) history.push(`/admin/profile/${userId}/${painArea.id}`);
                if (!isLoading) {
                  setTabPressed(true);
                  onClick(painArea.id);
                }
              }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!isMember) history.push(`/admin/profile/${userId}/${painArea.id}`);
                  if (!isLoading) {
                    setTabPressed(true);
                    onClick(painArea.id);
                  }
                }
              }}
              role="button"
              tabIndex={isLoading ? -1 : 0}
              aria-label={painArea.painAreaName}
              aria-disabled={isLoading}
            >
              <Tab title={painArea.painAreaName} loading={isLoading} />
            </div>
          )}
        </div>
      ))}
      {onPlusAction && (
        <div style={{ marginRight: "8px" }} key={"+"} id={"+"}>
          <div
            style={isLoading ? { cursor: "default" } : { cursor: "pointer" }}
            onClick={() => {
              onPlusAction && !isLoading && onPlusAction();
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onPlusAction && !isLoading && onPlusAction();
              }
            }}
            role="button"
            tabIndex={isLoading ? -1 : 0}
            aria-label="Add pain area"
            aria-disabled={isLoading}
          >
            <Tab title={"+"} shortTab />
          </div>
        </div>
      )}
    </div>
  );
};
